import * as authSelectors from '@redux/reducers/auth';
import { Fragment, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import {
  Button,
  ButtonDropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Tooltip
} from 'reactstrap';
import Icon from 'utility/icomoon';

interface ButtonIcon {
  key: number;
  id: string;
  icon?: string;
  action: any;
  title?: string;
  tooltipMessage?: string;
  isDisabled?: boolean;
  permissions?: any;
}

interface Props {
  id: string;
  iconSelected?: number;
  permissions?: any[];
  isDisabled?: boolean;
  className?: string;
  visible?: boolean;
  color?: string;
  buttonIcons: ButtonIcon[];
  iconFixed?: string;
  onlyIconDropDown?: boolean;
  tooltipMessage?: string;
  tooltipPlacement?: string;
}

const ButtonIconDropdown = (props: Props) => {
  const {
    isDisabled,
    iconSelected,
    id,
    permissions,
    color,
    visible = true,
    buttonIcons,
    iconFixed,
    onlyIconDropDown = false,
    tooltipMessage,
    tooltipPlacement = 'top'
  } = props;

  const intl = useIntl();

  const [isOpen, setIsOpen] = useState(false);
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const [tooltipOptionsOpen, setTooltipOptionsOpen] = useState<{ [key: string]: boolean }>({});
  const toggle = () => setTooltipOpen(!tooltipOpen);
  const store = useSelector((state) => state);

  const toggleTooltip = (index: string) => {
    setTooltipOptionsOpen((prevState) => ({
      ...prevState,
      [index]: !prevState[index]
    }));
  };

  const handleToggleClick = () => {
    setIsOpen(!isOpen);
    setTooltipOptionsOpen({});
  };

  const [activeIcon, setActiveIcon] = useState(iconSelected ?? 0);
  const renderIconItem = buttonIcons.map((buttonIcon, index) => {
    return !buttonIcon.permissions ||
      authSelectors.getAuthUserHasPermissionsAny(store, buttonIcon.permissions) ? (
      <Fragment key={buttonIcon.key || buttonIcon.id || `buttonOption${index}`}>
        <DropdownItem
          tag="li"
          id={onlyIconDropDown ? buttonIcon.id : `buttonOption${index}`}
          onClick={() => (onlyIconDropDown ? buttonIcon.action() : setActiveIcon(index))}
          className={`${buttonIcon.isDisabled ? 'disabled' : ''}`}
          tooltipmessage={buttonIcon.tooltipMessage}
        >
          {buttonIcon.title && <span>{buttonIcon.title}</span>}
          {buttonIcon.icon && <Icon icon={buttonIcon.icon} size={16} />}
        </DropdownItem>
        {onlyIconDropDown && buttonIcon.tooltipMessage && (
          <Tooltip
            key={`tooltipButtonOption${index}`}
            placement="right"
            isOpen={tooltipOptionsOpen[buttonIcon.id] || false} // Mostrar/ocultar según el estado
            target={buttonIcon.id}
            toggle={() => toggleTooltip(buttonIcon.id)}
            trigger="hover"
          >
            {buttonIcon.tooltipMessage}
          </Tooltip>
        )}
      </Fragment>
    ) : null;
  });

  return (!permissions || authSelectors.getAuthUserHasPermissionsAny(store, permissions)) &&
    visible ? (
    <>
      <ButtonDropdown
        isOpen={isOpen}
        toggle={handleToggleClick}
        className="dropdown-icon-wrapper"
        direction="down"
        disabled={isDisabled}
        id={onlyIconDropDown && id}
        key={'iconDropdown'}
      >
        {!onlyIconDropDown ? (
          <Button
            id={id}
            color={color ?? 'secondary'}
            onClick={() => buttonIcons[activeIcon].action()}
            className={`${isDisabled ? 'disabled' : ''}`}
            disabled={isDisabled}
          >
            {<Icon icon={buttonIcons[activeIcon].icon} size={16} />}
          </Button>
        ) : null}
        <DropdownToggle
          id={onlyIconDropDown && `${id}-dropdown-toggle`}
          className={`dropdown-toggle-split ${isDisabled ? 'disabled' : ''}`}
          color={color ?? 'secondary'}
          disabled={isDisabled}
          style={{ borderRadius: '8px' }}
          caret
        >
          {
            <Icon
              icon={
                onlyIconDropDown && iconFixed ? iconFixed : isOpen ? 'ChevronUp' : 'ChevronDown'
              }
              size={16}
            />
          }
        </DropdownToggle>
        {onlyIconDropDown && tooltipMessage ? (
          <Tooltip
            placement={tooltipPlacement}
            isOpen={tooltipOpen}
            target={`${id}-dropdown-toggle`} // Use the ID of DropdownToggle as the target
            toggle={toggle}
            trigger="hover"
          >
            {tooltipMessage}
          </Tooltip>
        ) : (
          <Tooltip
            placement={tooltipPlacement}
            isOpen={tooltipOpen}
            target={`${id}-dropdown-toggle`} // Use the ID of DropdownToggle as the target
            toggle={toggle}
            trigger="hover"
          >
            {intl.formatMessage({
              id: isOpen ? 'common.hideAllButtons' : 'common.showAllButtons'
            })}
          </Tooltip>
        )}
        <DropdownMenu tag="ul" right>
          {renderIconItem}
        </DropdownMenu>
      </ButtonDropdown>
      {!onlyIconDropDown && buttonIcons && buttonIcons.length > 0 && (
        <Tooltip
          placement="top"
          isOpen={tooltipOpen}
          target={id}
          toggle={toggle}
          trigger="click hover"
        >
          {buttonIcons[activeIcon].tooltipMessage}
        </Tooltip>
      )}
    </>
  ) : null;
};

ButtonIconDropdown.defaultProps = {
  id: 'ButtonIconDropdown',
  iconSelected: 0,
  isDisabled: false,
  className: ''
};

export default ButtonIconDropdown;
